import { ContentContainer } from 'app/components/Core';
import { ContentContainerType } from 'types/enums';
import { Box, Flex, Text, VStack, Image } from '@chakra-ui/react';
import {
  getFormattedDurationForPlaylist,
  getFormattedFilesCount,
} from 'app/components/Playlist/utils';
import { PlaylistHeaderImage } from 'app/components/Playlist/PlaylistHeaderImage';
import { AudioActionButton } from 'app/components/Song/Audio/AudioActionButton';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getAudioPlayer,
  getPlayerCurrentAudio,
} from 'app/store/AudioPlayer/selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAudioPlayer } from 'app/components/AudioPlayer/hooks';
import { SharedPlaylist } from 'types/shares.types';
import { AudioPlayerAudioInstance } from 'app/store/AudioPlayer/slice.types';
import { setQueueIndexAction } from 'app/store/AudioPlayer/slice';
import { ShareResponse, ShareType } from 'app/components/Shares/types';
import { SongHeaderBg } from 'app/components/Song';

interface AudioShareHeaderProps {
  sharedPlaylist: SharedPlaylist;
  shareType?: ShareType;
  song?: ShareResponse['response']['song']['item'];
}

export const AudioShareHeader = ({
  sharedPlaylist,
  shareType,
  song,
}: AudioShareHeaderProps) => {
  const { guid, title, queue, images } = sharedPlaylist;
  const totalDuration = queue.reduce(
    (acc, curr) => acc + (curr.duration ?? 0),
    0
  );
  const formattedDuration = getFormattedDurationForPlaylist(totalDuration);
  const formattedCount = getFormattedFilesCount(queue.length);

  const { isPlaying } = useAppSelector(getAudioPlayer);
  const currentAudio = useAppSelector(getPlayerCurrentAudio);

  const [playingAudio, setPlayingAudio] = useState<AudioPlayerAudioInstance>(
    queue?.[0]
  );

  useEffect(() => {
    if (!currentAudio) {
      return;
    }

    const audio = queue?.find(({ audioGuid }) =>
      [audioGuid].includes(currentAudio.audioGuid)
    );
    if (audio) {
      setPlayingAudio(audio);
    }
  }, [currentAudio, queue]);

  const audioGuid = useMemo(() => playingAudio?.audioGuid, [
    playingAudio?.audioGuid,
  ]);

  const { playPauseAudio } = useAudioPlayer();

  const dispatch = useAppDispatch();
  const setQueueIndex = useCallback(
    (index: number) => dispatch(setQueueIndexAction(index)),
    [dispatch]
  );

  const handlePlay = async () => {
    if (!playingAudio) {
      return;
    }

    if (!audioGuid) {
      return;
    }

    if (currentAudio?.audioGuid === audioGuid) {
      playPauseAudio();
    } else {
      setQueueIndex(0);
    }
  };

  const isLoading = useMemo(
    () => currentAudio?.audioGuid === audioGuid && currentAudio?.loading,
    [audioGuid, currentAudio?.audioGuid, currentAudio?.loading]
  );

  const image = useMemo(
    () => images.find((image) => image.name === 'playlist-small'),
    [images]
  );

  return (
    <Box
      bg="surface1"
      zIndex={1101}
      position="relative"
      data-testid="share-header"
    >
      {song ? (
        <SongHeaderBg status={song?.status} />
      ) : (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            w: 'full',
            h: 'full',
            overflow: 'hidden',
            bg: 'M.30',
          }}
        >
          <Box
            sx={{
              h: 200,
              opacity: 0.36,
              filter: `blur(64px)`,
            }}
          >
            {image && (
              <Image
                src={image.href}
                w="100%"
                h="unset"
                mt="64px"
                transform="translate(0, -50%)"
                aspectRatio="1/1"
              />
            )}
          </Box>
        </Box>
      )}

      <ContentContainer type={ContentContainerType.LIST} py={6}>
        <Flex>
          <PlaylistHeaderImage
            images={images}
            playlistGuid={guid}
            song={song}
            readOnly
          />
          <VStack pl={4} alignItems="baseline" spacing={0} overflow="hidden">
            <Text fontSize="xxs" fontWeight={500} opacity={0.72}>
              {shareType === ShareType.PLAYLIST ? 'Playlist' : 'Audio'}
            </Text>
            <Text
              fontSize="2xl"
              fontWeight={500}
              w="full"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              data-testid="share-header-title"
            >
              {title}
            </Text>
            {queue.length > 0 && (
              <Text fontSize="xs">
                <span data-testid="author">
                  Shared by {sharedPlaylist.author}
                </span>
                &nbsp;•&nbsp;
                <span data-testid="file-count">{formattedCount}</span>
                &nbsp;•&nbsp;
                <span data-testid="total-duration">{formattedDuration}</span>
              </Text>
            )}
            <Flex gap={4} justifyContent="center" pt={2}>
              <AudioActionButton
                playing={isPlaying && currentAudio.audioGuid === audioGuid}
                onClick={handlePlay}
                data-testid="audio-play-pause-button"
                isLoading={isLoading}
                size={32}
                isPlayerTheme
                playIconTestId="audio-play-icon"
                pauseIconTestId="audio-pause-icon"
                thickness={2}
                isDisabled={queue.length === 0}
              />
            </Flex>
          </VStack>
        </Flex>
      </ContentContainer>
    </Box>
  );
};
